import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { EMPTY, map, Observable, of } from "rxjs";
import {
    AddThreadTaskActionDTO,
    AgreeDisagreePollResultTask,
    ArchiveTaskActionDTO,
    ClosedPollResultTask,
    CreateKudosTaskActionDTO,
    CreatePollTaskActionDTO,
    FiveStarsPollResultTask,
    KudosTask,
    MakePollRecurrentTaskActionDTO,
    MoodTask,
    PollResultTask,
    Recurrence,
    Review,
    ScalePollResultTask,
    Task,
    TaskAction,
    TaskActionDTO,
    TaskAssignee,
    Taskbook,
    TaskLabel,
    TaskListItem,
    TopicTask
} from "./taskbook.types";
import { Kudos, MoodValue, RichTextComment } from "@teammood/timeline";
import { SavedFilterChange } from "../filter/saved-filter/saved-filter.component";
import { CompositeFilter } from "@teammood/filter";
import { SnackBarService } from "@teammood/shared/ui/snack-bar";
import { PollUnderCreation } from "../polls/create-poll/create-poll.type";
import Delta from "quill-delta";
import { ReactionService } from "../timeline/timeline-item/reaction/reaction.service";
import { TranslateService } from "@ngx-translate/core";
import { UserActivityService } from "../timeline/user-activity/user-activity.service";
import { PollListItem } from "../polls/user-polls/user-polls.type";

@Injectable({
    providedIn: 'root'
})
export class TaskbookService {

    constructor(
        private http: HttpClient, 
        private snackBarService: SnackBarService, 
        private translateService: TranslateService,
        private reactionService: ReactionService,
        private userActivityService: UserActivityService
    ) { }

    tasks(filter: SavedFilterChange): Observable<{managers: TaskAssignee[], taskList: TaskListItem[]}> {
        if (filter) {
            return this.http.post<Taskbook>('/api/taskbook', {predicates: filter.filter.decodedFilter.predicates}).pipe(
                map(taskbook => {
                    if (taskbook.error && filter.filter.decodedFilter.predicates.length > 0) {
                        this.snackBarService.error(taskbook.error);
                        return;
                    } else {
                        return {
                            managers: taskbook.managers,
                            taskList: this.transformTasksToDiscussionItems(taskbook.tasks)
                        }
                    }
                })
            )
        } 
        // else {
        //     return of([]);
        // }
    }

    triage(filter: SavedFilterChange): Observable<Task> {
        if (filter) {
            return this.http.post<Task>('/api/triage', {predicates: filter.filter.decodedFilter.predicates});
        } else {
            return EMPTY;
        }
    }


    triageInfos(filter: SavedFilterChange): Observable<{ count: number }> {
        if (filter) {
            return this.http.put<{ count: number }>('/api/triage/infos', {predicates: filter.filter.decodedFilter.predicates});
        } else {
            return EMPTY;
        }
    }

    taskDetails(itemType: string, itemId: string): Observable<{task: Task, managers: any[]}> {
        return this.http.get<{task: Task, managers: any[]}>(`/api/backlog/${itemType}/${itemId}`).pipe(
            map(task => {
                return {
                    task: Task.createTask(task.task),
                    managers: task.managers,
                }
            })
        );
    }

    pollTaskDetails(aggregateId: string): Observable<PollListItem> {
        return this.http.get<PollListItem>(`/api/question/aggregate/${aggregateId}`);
    }

    availableLabels(): Observable<TaskLabel[]> {
        return this.http.get<TaskLabel[]>(`/api/backlog/label/all`);
    }

    deleteLabel(label: TaskLabel): Observable<void> {
        return this.http.delete<void>(`/api/backlog/label`, {
            body: label
        });
    }

    editLabel(label: TaskLabel): Observable<TaskLabel> {
        return this.http.put<TaskLabel>(`/api/backlog/label`, label);
    }

    getTaskReactions(taskListItem: TaskListItem){
        const reactions = this.reactionService.mapToEmoticonReactions(taskListItem.task.reactions)
        let totalCount = 0;
        let description = '';
        let htmlReactionIcons = '';
        reactions.forEach(reaction => {
            totalCount += reaction.count;
            if (reaction.count > 0) {
                description += reaction.count + ' ' + this.translateService.instant(reaction.label) + ' ' + ' \n';
                htmlReactionIcons += reaction.smiley ? reaction.smiley : '<i class="' + reaction.icon + '"></i>';
            }
        })
        return {
            allReactionCount: totalCount,
            allReactionDescription: description,
            allReactionHtmlIcons: htmlReactionIcons
        }        
    }

    // mettre a la racine de l'objet
    setLabel(task: Task, actionId: string){
        if (!task.error) {
            switch(actionId){
                case 'thread':
                    return this.setDiscussLabel(task.itemType, task.threads.length > 0);
                case 'kudos':
                    return {
                        title: 'task_action.kudos',
                        description: 'task_action.kudos_desc'
                    };
                case 'poll':
                    return {
                        title: 'task_action.poll',
                        description: 'task_action.poll_desc'
                    };
                case 'makePollrecurrent':
                    return {
                        title: 'task_action.makePollrecurrent',
                        description: 'task_action.makePollrecurrent_desc'
                    };
                case 'report':
                    return {
                        title: 'task_action.report',
                        description: 'task_action.report_desc'
                    };
                case 'keep':
                    return {
                        title: 'task_action.keep',
                        description: 'task_action.keep_desc'
                    };
                case 'skip':
                    return {
                        title: 'task_action.skip',
                        description: 'task_action.skip_desc'
                    };
                case 'archive':
                    return this.setArchivetLabel(task);

            }
        }
    }

    setArchivetLabel(task: Task): { title: string, description: string}{
        if(task.reviews.length === 0){
            return {
                title: 'task_action.archive_without_action',
                //description: null
                description: 'task_action.archive_without_action_desc'
            }; 
        }else{
            return {
                title: 'task_action.archive_after_action',
                description: null
                // description: 'task_action.archive_after_action_desc'
            }; 
        }
    }

    setDiscussLabel(itemType: string, discussionStarted: boolean): { title: string, description: string}{
        switch(itemType){
            case 'moodItem':
                return {
                    title: discussionStarted ? 'task_action.discuss_mood' : 'task_action.start_discussion',
                    description: 'task_action.start_discussion_desc'
                }; 
            case 'kudosItem':
                return {
                    title: discussionStarted ? 'task_action.discuss_kudos' : 'task_action.start_discussion',
                    description: 'task_action.start_discussion_desc'
                }; 
            case 'topicItem':
                return {
                    title: discussionStarted ? 'task_action.discuss_topic' : 'task_action.start_discussion',
                    description: 'task_action.start_discussion_desc'
                }; 
            case 'badMoodStreakItem':
                return {
                    title: 'task_action.discuss_badMoodStreak',
                    description: 'task_action.start_discussion_desc'
                }; 
            case 'closedQuestionResultItem':
                return {
                    title: 'task_action.discuss_results',
                    description: 'task_action.start_discussion_desc'
                }; 
            case 'agreeDisagreeQuestionResultItem':
                return {
                    title: 'task_action.discuss_results',
                    description: 'task_action.start_discussion_desc'
                }; 
            case 'scaleQuestionResultItem':
                return {
                    title: 'task_action.discuss_results',
                    description: 'task_action.start_discussion_desc'
                }; 
            case 'fiveStarsQuestionResultItem':
                return {
                    title: 'task_action.discuss_results',
                    description: 'task_action.start_discussion_desc'
                }; 
        }
    }

    getActionsByTaskType(task: Task): TaskAction[]{
        const discussAction: TaskAction = {
            id: 'thread',
            label: this.setLabel(task, 'thread'),
            class: 'btn-discuss',
            icon: 'fa-comments',
            operableInDialog: false,
            createDTO: (task: Task, filter: CompositeFilter, newThread: { message: RichTextComment, isAnonymous: boolean}) => new AddThreadTaskActionDTO(task.id, task.itemType, filter, newThread.message, newThread.isAnonymous)
        }

        const archiveAction: TaskAction = {
            id: 'archive',
            label: this.setLabel(task, 'archive'),
            class: 'btn-archive',
            icon: 'fa-box-archive',
            operableInDialog: false,
            createDTO: (task: Task, filter: CompositeFilter) => new ArchiveTaskActionDTO(task.id, task.itemType, filter)
        }

        const kudosAction: TaskAction = {
            id: 'kudos',
            label: this.setLabel(task, 'kudos'),
            class: 'btn-action btn-kudos',
            icon: 'fa-stars',
            operableInDialog: true,
            createDTO: (task: Task, filter: CompositeFilter, kudos: Kudos, teamId) => new CreateKudosTaskActionDTO(task.id, task.itemType, filter, kudos, teamId)

        }

        const pollRecurrenceAction: TaskAction = {
            id: 'makePollrecurrent',
            label: this.setLabel(task, 'makePollrecurrent'),
            class: 'btn-action btn-pool',
            icon: 'fa-arrows-repeat',
            operableInDialog: true,
            createDTO: (task: PollResultTask, filter: CompositeFilter, recurrence: Recurrence) => {
                return new MakePollRecurrentTaskActionDTO(task.id, task.itemType, filter, recurrence, task.pollTargets.map(t => t.id))
            }
        }

        const pollAction: TaskAction = {
            id: 'poll',
            label: this.setLabel(task, 'poll'),
            class: 'btn-action btn-pool',
            icon: 'fa-square-poll-horizontal',
            operableInDialog: true,
            createDTO: (task: Task, filter: CompositeFilter, poll: PollUnderCreation) => new CreatePollTaskActionDTO(task.id, task.itemType, filter, poll)
        }

        switch(task.itemType){
            case 'moodItem':
                return [discussAction, pollAction, kudosAction, archiveAction];
            case 'kudosItem':
                return [discussAction, pollAction, kudosAction, archiveAction];
            case 'topicItem':
                return [discussAction, pollAction, kudosAction, archiveAction];
            case 'badMoodStreakItem':
                return [discussAction, archiveAction];
            case 'closedQuestionResultItem':
                if((task as ClosedPollResultTask).pollTargets.length > 1){
                    return [discussAction, pollRecurrenceAction, kudosAction, archiveAction];
                }else{
                    return [discussAction, kudosAction, archiveAction];
                }
            case 'agreeDisagreeQuestionResultItem':
                if((task as AgreeDisagreePollResultTask).recurrence.frequency === 'once'){
                    return [discussAction, pollRecurrenceAction, kudosAction, archiveAction];
                }else{
                    return [discussAction, kudosAction, archiveAction];
                }
            case 'scaleQuestionResultItem':
                if((task as ScalePollResultTask).recurrence.frequency === 'once'){
                    return [discussAction, pollRecurrenceAction, kudosAction, archiveAction];
                }else{
                    return [discussAction, kudosAction, archiveAction];
                }
            case 'fiveStarsQuestionResultItem':
                if((task as FiveStarsPollResultTask).recurrence.frequency === 'once'){
                    return [discussAction, pollRecurrenceAction, kudosAction, archiveAction];
                }else{
                    return [discussAction, kudosAction, archiveAction];
                }
            }

    }

    getArchiveDetailActions(task: Task): TaskAction{
        const archiveAction: TaskAction = {
            id: 'archive',
            label: this.setLabel(task, 'archive'),
            class: 'btn-archive',
            icon: 'fa-box-archive',
            operableInDialog: false,
            createDTO: (task: Task, filter: CompositeFilter) => new ArchiveTaskActionDTO(task.id, task.itemType, filter)
        }
        return archiveAction;
    }

    getDetailActionsByTaskType(task: Task): TaskAction[]{
        const discussAction: TaskAction = {
            id: 'thread',
            label: {title: 'task_action.reply', description: ''},
            class: 'btn-action btn-discuss',
            icon: 'fa-comments',
            operableInDialog: false,
            createDTO: (task: Task, filter: CompositeFilter, newThread: { message: RichTextComment, isAnonymous: boolean}) => new AddThreadTaskActionDTO(task.id, task.itemType, filter, newThread.message, newThread.isAnonymous)
        }


        const kudosAction: TaskAction = {
            id: 'kudos',
            label: this.setLabel(task, 'kudos'),
            class: 'btn-action btn-kudos',
            icon: 'fa-stars',
            operableInDialog: true,
            createDTO: (task: Task, filter: CompositeFilter, kudos: Kudos, teamId) => new CreateKudosTaskActionDTO(task.id, task.itemType, filter, kudos, teamId)

        }

        const pollAction: TaskAction = {
            id: 'poll',
            label: this.setLabel(task, 'poll'),
            class: 'btn-action btn-poll',
            icon: 'fa-square-poll-horizontal',
            operableInDialog: true,
            createDTO: (task: Task, filter: CompositeFilter, poll: PollUnderCreation) => new CreatePollTaskActionDTO(task.id, task.itemType, filter, poll)
        }

        return [discussAction, pollAction, kudosAction];
    }

    getFilteredReviews(reviews: Review[], userId: string): Review[]{
        const orderedByTime = reviews.slice().reverse();
        const removedReadAndMentionedReviews = orderedByTime.filter(review => {
            return review.reviewType !== 'ReadReview' && review.reviewType !== 'MentionedReview'
        });
        const removedNewThreadReviewsIfNotMe = removedReadAndMentionedReviews.filter(review => {
            if(review.reviewType === 'NewThreadReview'){
                return review.reviewer.id === userId
            }else{
                return true;
            }
        });
        return removedNewThreadReviewsIfNotMe;
    }

    saveAction(actions: TaskActionDTO[]) {
        return this.http.put<Taskbook>('/api/taskbook/taskAction', actions).pipe(
            map(taskbook => {
                if (taskbook.error) {
                    this.snackBarService.error(taskbook.error);
                    return [];
                } else {
                    return this.transformTasksToDiscussionItems(taskbook.tasks);
                }
            })
        )
    }

    taskDetailAction(action: TaskActionDTO) {
        return this.http.put<Task>('/api/taskbook/details/taskAction', action).pipe(
            map(task => {
                return this.transformTaskToDiscussionItems(task).task;
            })
        )
    }

    triageAction(action: TaskActionDTO) {
        return this.http.put<Task>('/api/triage/taskAction', action)
    }

    deleteReview(itemType: string, itemId: string, review: Review) {
        return this.http.delete<{result: string}>(`/api/backlog/${itemType}/${itemId}/${review.id}`);
    }

    updateManagerCommentReview(itemType: string, itemId: string, review: Review, update: Delta) {
        return this.http.put<Task>(`/api/backlog/${itemType}/${itemId}/${review.id}`, update);
    }

    private transformTasksToDiscussionItems(tasks: Task[]): TaskListItem[] {
        return tasks.map(task => this.transformTaskToDiscussionItems(task));
    }

    private transformTaskToDiscussionItems(task: Task): TaskListItem {
            // Initialisation de l'objet TaskListItem
            const discussionItem: TaskListItem = new TaskListItem(task);

            // Vérification du type de tâche et ajout du contenu approprié
            switch (task.itemType) {
                case 'moodItem':
                    discussionItem.mood = {
                        value: (task as MoodTask).mood,
                        iconPath: this.build_icon_path((task as MoodTask).mood)
                    }
                    discussionItem.listContent.body = this.userActivityService.formatActivityLink((task as MoodTask).comment.body);
                    break;

                case 'badMoodStreakItem':
                    discussionItem.listContent.body = "Bad Mood Streak";  // Exemple pour les tâches de mauvaise humeur
                    break;

                case 'kudosItem':
                    discussionItem.listContent.body = this.userActivityService.formatActivityLink((task as KudosTask).message.body);  // Ajout du message pour KudosTask
                    break;

                case 'topicItem':
                    discussionItem.listContent.body = this.userActivityService.formatActivityLink((task as TopicTask).title.body);  // Ajout du titre pour TopicTask
                    break;

                case 'agreeDisagreeQuestionResultItem':
                case 'closedQuestionResultItem':
                case 'scaleQuestionResultItem':
                case 'fiveStarsQuestionResultItem':
                    discussionItem.results = (task as AgreeDisagreePollResultTask).results;
                    discussionItem.listContent.body = this.userActivityService.formatActivityLink((task as AgreeDisagreePollResultTask).label);  // Ajout du label pour les résultats de sondage
                    break;

                default:
                    discussionItem.listContent.body = "Unknown Task Type";  // Gestion des types non définis
                    break;
            }

            return discussionItem;
    }

    build_icon_path(mood: MoodValue): string {
        return `/assets/images/emoticons/${mood}.svg`;
    }

}
